import { API_BASE_URL } from '../config/api.config';
import { UserProfile } from '../models/user-profile.model';

/**
 * Auth service — delegates everything to the backend.
 *
 * The backend handles the full OAuth2 Authorization Code flow
 * (confidential client). The SPA only needs to redirect the browser
 * to the backend's login/logout endpoints.
 *
 * Session state lives in an HttpOnly cookie managed by the backend.
 * The SPA never sees tokens.
 */
class AuthService {
  login(): void {
    window.location.href = `${API_BASE_URL}/oauth2/login`;
  }

  logout(): void {
    window.location.href = `${API_BASE_URL}/oauth2/logout`;
  }

  /**
   * Fetch the authenticated user's profile, or null when there is no
   * active session. Used at bootstrap to seed the user store and to
   * resolve the UI language from the profile's languageCode.
   */
  async getSession(): Promise<UserProfile | null> {
    try {
      const res = await fetch(`${API_BASE_URL}/api/user/me`, {
        method: 'GET',
        credentials: 'include',
      });
      if (!res.ok) return null;
      return (await res.json()) as UserProfile;
    } catch {
      return null;
    }
  }

  /**
   * Check if the user has an active session by calling the backend.
   * Returns true if the backend responds 200 on a lightweight endpoint.
   */
  async isAuthenticated(): Promise<boolean> {
    return (await this.getSession()) !== null;
  }
}

export const authService = new AuthService();
